// Per-tab persistence of sort, filter, search, and page so users can
// navigate away and return without losing their place. Scoped per
// pathname + table caption so the same component on a different page
// gets a clean state — except that date-range path suffixes are stripped
// from the pathname first (see `buildStorageKey`), so a date-ranged table
// keeps one range-agnostic key and its sort/filter/search survive range
// changes. Per-tab via sessionStorage so a fresh window starts fresh.

import { slugify } from './helpers';

// Bump only on a *breaking* change to the stored shape. v2 nests criteria by
// kind (`{ field: { checkbox, number, search } }`) so a column can persist
// more than one narrowing tool at once — incompatible with v1's flat
// `{ field: criteria }`, so `loadTableState` rejects stale v1 snapshots
// (users lose persisted filters once) rather than misreading them.
const STORAGE_VERSION = 2;
const STORAGE_PREFIX = 'amba-tabulator';

// Date-ranged remote tables (usage logs, alert outcomes, …) carry the selected
// range IN the pathname (see `config/routes.rb`:
// `last-:duration_amount-:duration_unit`, `:start_date/:end_date`,
// `:single_date`). If the storage key included that suffix, every range change
// would land on a different key and silently drop the user's saved
// sort/filter/search. Strip the range suffix so the key is range-agnostic and
// persistence survives switching ranges. These mirror the refresher
// controller's `DATE_PATH_SUFFIX_RES`; only one is ever anchored-matched, and
// we keep them local here rather than importing to keep persistence
// self-contained.
const DATE_RANGE_PATH_SUFFIXES = [
  /\/last-\d+-\w+$/,
  /\/\d{4}-\d{2}-\d{2}\/\d{4}-\d{2}-\d{2}$/,
  /\/\d{4}-\d{2}-\d{2}$/,
];

function buildStorageKey(caption) {
  let pathname = window.location.pathname;
  DATE_RANGE_PATH_SUFFIXES.forEach((re) => {
    pathname = pathname.replace(re, '');
  });
  return `${STORAGE_PREFIX}:${pathname}:${slugify(caption)}`;
}

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

// Returns the stored snapshot or `null`. `null` covers all the "don't
// restore" paths: no entry, JSON parse failure, schema mismatch, or
// sessionStorage being unavailable (private browsing, disabled storage).
export function loadTableState(caption) {
  let raw;
  try {
    raw = sessionStorage.getItem(buildStorageKey(caption));
  } catch {
    return null;
  }
  if (!raw) return null;
  const parsed = safeParse(raw);
  if (!parsed || parsed.v !== STORAGE_VERSION) return null;
  return parsed;
}

export function saveTableState(caption, snapshot) {
  try {
    sessionStorage.setItem(
      buildStorageKey(caption),
      JSON.stringify({ v: STORAGE_VERSION, ...snapshot })
    );
  } catch {
    // Quota exceeded / privacy mode — persistence is a nice-to-have,
    // not a hard requirement, so silently drop the write.
  }
}

// Nested `Map<field, Map<kind, criteria>>` → JSON-safe object shaped
// `{ field: { checkbox, number, search } }`. Each field can carry more than
// one kind (e.g. a checkbox filter plus a search). Sets become arrays; the
// kind is implied by the key, so it's dropped from the stored value and
// re-attached on deserialize.
export function serializeFilterState(filterStateMap) {
  const out = {};
  for (const [field, kinds] of filterStateMap) {
    const fieldOut = {};
    for (const [kind, criteria] of kinds) {
      if (kind === 'checkbox') {
        fieldOut.checkbox = {
          checkedValues: [...criteria.checkedValues],
          allOptions: criteria.allOptions,
          // Restoring without this would compare a multi-value cell as one
          // opaque string and hide every row it belongs to.
          multiValue: criteria.multiValue === true,
        };
      } else if (kind === 'number') {
        fieldOut.number = {
          operator: criteria.operator,
          value: criteria.value,
        };
      } else if (kind === 'search') {
        fieldOut.search = { query: criteria.query };
      } else if (kind === 'keywords') {
        fieldOut.keywords = {
          checkedValues: [...criteria.checkedValues],
          allOptions: criteria.allOptions,
        };
      }
    }
    if (Object.keys(fieldOut).length > 0) out[field] = fieldOut;
  }
  return out;
}

export function deserializeFilterState(stored) {
  const map = new Map();
  if (!stored) return map;
  for (const [field, fieldState] of Object.entries(stored)) {
    const kinds = new Map();
    if (fieldState.checkbox) {
      kinds.set('checkbox', {
        kind: 'checkbox',
        checkedValues: new Set(fieldState.checkbox.checkedValues ?? []),
        allOptions: fieldState.checkbox.allOptions ?? [],
        multiValue: fieldState.checkbox.multiValue === true,
      });
    }
    if (fieldState.number) {
      kinds.set('number', {
        kind: 'number',
        operator: fieldState.number.operator ?? '=',
        value: fieldState.number.value ?? '',
      });
    }
    if (fieldState.search) {
      kinds.set('search', {
        kind: 'search',
        query: fieldState.search.query ?? '',
      });
    }
    if (fieldState.keywords) {
      kinds.set('keywords', {
        kind: 'keywords',
        checkedValues: new Set(fieldState.keywords.checkedValues ?? []),
        allOptions: fieldState.keywords.allOptions ?? [],
      });
    }
    if (kinds.size > 0) map.set(field, kinds);
  }
  return map;
}
